/*
 * Collection "Weight" sorts in carat weight order (the owner, 2026-09-28).
 *
 * "Weight, low to high" and "Weight, high to low" are Shopify's name sorts (sort_by=title-ascending and
 * title-descending) relabeled in snippets/functions.liquid: product titles start with the weight, so name
 * order was used as weight order, and a 22ct necklace sorted between 2ct and 3ct. With the theme setting
 * "Sort Weight by carat weight" on, each page of those sorts is rebuilt here in true weight order:
 *
 *   1. The weights: templates/collection.weight-index.liquid lists every product of the collection, with
 *      the page's filters applied by Shopify itself, in name order, 250 per request. Kept in the tab's
 *      session storage for 10 minutes.
 *   2. The weight of a product: Total Carat Weight; when blank, center diamond + side diamonds; when both
 *      are blank, zero (the owner, 2026-09-28). Zero comes first low to high and last high to low. Equal
 *      weights keep name order.
 *   3. The page: the products that belong on page N in weight order, as the same cards the collection
 *      draws, taken from the collection's own name-ordered pages (fetched as the theme's filters fetch
 *      them). Same page size, same count, same page links, same end-of-list tiles.
 *
 * Whatever cannot be done (a request fails, the counts disagree, a card is missing) leaves the page
 * exactly as Shopify served it, in name order, which is how it looked before.
 *
 * Hooked into theme.CollectionFilters.renderSectionFromFetch (assets/theme.js): every sort, filter and
 * page change passes through transform(). A first load in a Weight sort goes through the same path.
 */
(function () {
  'use strict';

  var PENDING = 'hw-weight-pending';
  var INDEX_TTL_MS = 10 * 60 * 1000;
  var INDEX_KEEP = 8;
  var TIMEOUT_MS = 12000;
  var CONCURRENCY = 4;

  function settle() {
    document.documentElement.classList.remove(PENDING);
  }

  // ─── The weight of a product ───────────────────────────────────────────────

  function toNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    var n = Number(value);
    return isFinite(n) ? n : null;
  }

  /** Total Carat Weight; when blank, center + side; when both are blank, zero. */
  function weightOf(total, center, side) {
    var t = toNumber(total);
    if (t !== null) return t;
    var c = toNumber(center), s = toNumber(side);
    if (c === null && s === null) return 0;
    // Rounded so 0.1 + 0.2 ties with 0.3.
    return Math.round(((c || 0) + (s || 0)) * 1e6) / 1e6;
  }

  /** Rows [id, weight] in name order, ranked: by weight, then name order. */
  function rank(rows, dir) {
    return rows
      .map(function (row, pos) { return { id: row[0], w: row[1], pos: pos }; })
      .sort(function (a, b) { return (dir === 'desc' ? b.w - a.w : a.w - b.w) || a.pos - b.pos; });
  }

  // ─── Requests ──────────────────────────────────────────────────────────────

  function fetchText(url, signal) {
    return fetch(url, { credentials: 'same-origin', signal: signal }).then(function (response) {
      if (!response.ok) throw new Error(response.status + ' ' + url);
      return response.text();
    });
  }

  /** Runs `work` over `items`, a few at a time. */
  function eachLimited(items, limit, work) {
    var next = 0;
    function lane() {
      if (next >= items.length) return Promise.resolve();
      var item = items[next++];
      return work(item).then(lane);
    }
    var lanes = [];
    for (var i = 0; i < Math.min(limit, items.length); i++) lanes.push(lane());
    return Promise.all(lanes);
  }

  /** The filter.* part of an address, in a fixed order: what the index and the name pages depend on. */
  function filterQuery(params) {
    var pairs = [];
    params.forEach(function (value, key) { if (key.indexOf('filter.') === 0) pairs.push([key, value]); });
    pairs.sort(function (a, b) { return a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0; });
    return pairs.map(function (p) { return encodeURIComponent(p[0]) + '=' + encodeURIComponent(p[1]); }).join('&');
  }

  // ─── The weights of a collection ───────────────────────────────────────────

  function storageGet(key) {
    try {
      var entry = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      return entry && Date.now() - entry.t < INDEX_TTL_MS ? entry : null;
    } catch (e) {
      return null;
    }
  }

  function storagePut(key, entry) {
    try {
      var keysKey = 'hwws:keys';
      var keys = JSON.parse(window.sessionStorage.getItem(keysKey) || '[]').filter(function (k) { return k !== key; });
      keys.push(key);
      while (keys.length > INDEX_KEEP) window.sessionStorage.removeItem(keys.shift());
      window.sessionStorage.setItem(key, JSON.stringify(entry));
      window.sessionStorage.setItem(keysKey, JSON.stringify(keys));
    } catch (e) {
      /* storage full or off: the weights are fetched again next time */
    }
  }

  /** [[id, weight], ...] for every product, in name order. `expected` is the collection's own count. */
  function loadWeights(path, filters, expected, signal) {
    var key = 'hwws:v1:' + path + '?' + filters;
    var cached = storageGet(key);
    if (cached && cached.n === expected) return Promise.resolve(cached.rows);

    var base = path + '?view=weight-index&sort_by=title-ascending' + (filters ? '&' + filters : '') + '&page=';
    return fetchText(base + 1, signal).then(function (text) {
      var first = JSON.parse(text);
      var windows = [first];
      var rest = [];
      for (var k = 2; k <= first.pages; k++) rest.push(k);
      return eachLimited(rest, CONCURRENCY, function (k) {
        return fetchText(base + k, signal).then(function (t) { windows[k - 1] = JSON.parse(t); });
      }).then(function () {
        var rows = [];
        var seen = {};
        windows.forEach(function (w) {
          // Every request must describe the same list, and the one the page was drawn from.
          if (!w || w.items !== expected) throw new Error('weight index count ' + (w && w.items) + ' != ' + expected);
          w.p.forEach(function (r) {
            var id = String(r[0]);
            if (seen[id]) throw new Error('weight index repeats ' + id);
            seen[id] = true;
            rows.push([id, weightOf(r[1], r[2], r[3])]);
          });
        });
        if (rows.length !== expected) throw new Error('weight index rows ' + rows.length + ' != ' + expected);
        storagePut(key, { t: Date.now(), n: expected, rows: rows });
        return rows;
      });
    });
  }

  // ─── Cards ─────────────────────────────────────────────────────────────────

  // Cards seen on name-ordered pages, by filters then product id, as markup. The same product can draw
  // differently under a filter (a metal filter picks its variant), so each filter set keeps its own.
  var cardStore = {};
  var cardStoreOrder = [];

  function cardsFor(filters) {
    if (!cardStore[filters]) {
      cardStore[filters] = {};
      cardStoreOrder.push(filters);
      while (cardStoreOrder.length > 6) delete cardStore[cardStoreOrder.shift()];
    }
    return cardStore[filters];
  }

  function productCards(root) {
    var grid = root.getElementById ? root.getElementById('main-collection-product-grid') : root.querySelector('#main-collection-product-grid');
    return grid ? Array.prototype.slice.call(grid.children).filter(function (el) { return el.classList.contains('js-product-listing'); }) : [];
  }

  function keepCards(store, root) {
    productCards(root).forEach(function (card) {
      var id = card.getAttribute('data-product-id');
      if (id) store[id] = card.outerHTML;
    });
  }

  // ─── A page in weight order ────────────────────────────────────────────────

  function build(html, url, signal) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var grid = doc.getElementById('main-collection-product-grid');
    if (!grid || !grid.getAttribute('data-weight-sort')) return Promise.resolve(null);

    var dir = grid.getAttribute('data-weight-sort');
    var path = grid.getAttribute('data-collection-path');
    var sectionId = grid.getAttribute('data-id');
    var size = Number(grid.getAttribute('data-page-size'));
    var expected = Number(grid.getAttribute('data-items'));
    var page = Number(grid.getAttribute('data-page')) || 1;
    if (!path || !sectionId || !(size > 0) || !(expected >= 0)) return Promise.reject(new Error('grid attributes'));

    var filters = filterQuery(new URL(url, window.location.origin).searchParams);
    var served = productCards(doc);
    var store = cardsFor(filters);
    keepCards(store, doc);

    var indexKey = 'hwws:v1:' + path + '?' + filters;
    return loadWeights(path, filters, expected, signal).then(function (rows) {
      var ranked = rank(rows, dir);
      var slice = ranked.slice((page - 1) * size, page * size);
      // The page Shopify served holds the same number of products as the page in weight order.
      if (slice.length !== served.length) throw new Error('page holds ' + served.length + ', weight order ' + slice.length);

      // Name-ordered pages that hold the cards not yet seen, most needed first.
      var need = {};
      slice.forEach(function (item) {
        if (store[item.id]) return;
        var p = Math.floor(item.pos / size) + 1;
        need[p] = (need[p] || 0) + 1;
      });
      var pages = Object.keys(need).sort(function (a, b) { return need[b] - need[a]; });
      var base = path + '?section_id=' + encodeURIComponent(sectionId) + '&sort_by=title-ascending' + (filters ? '&' + filters : '') + '&page=';
      return eachLimited(pages, CONCURRENCY, function (p) {
        return fetchText(base + p, signal).then(function (text) {
          keepCards(store, new DOMParser().parseFromString(text, 'text/html'));
        });
      }).then(function () {
        var holder = doc.createElement('div');
        var cards = slice.map(function (item) {
          var markup = store[item.id];
          if (!markup) throw new Error('no card for ' + item.id);
          holder.innerHTML = markup;
          return holder.firstElementChild;
        });
        // Out go the name-ordered cards; in go the weight-ordered ones, before whatever follows the
        // products (the end-of-list Custom Designs and Most Loved tiles).
        var after = served.length ? served[served.length - 1].nextElementSibling : grid.firstElementChild;
        served.forEach(function (card) { card.remove(); });
        cards.forEach(function (card) { grid.insertBefore(card, after); });
        // This list is in weight order again: its page links continue in weight order (review, round 6).
        forgetAsServed(asServedKey(path, dir, filters));
        return '<!doctype html>' + doc.documentElement.outerHTML;
      });
    }).catch(function (error) {
      // The index kept in this tab may be what went wrong: it is reused while the collection's count is
      // the same, so a product swapped for another, or a weight staff just corrected, left every Weight
      // page of this tab in name order until it expired (review, round 5). The next page fetches it anew.
      try { window.sessionStorage.removeItem(indexKey); } catch (e) { /* storage off */ }
      throw error;
    });
  }

  /**
   * The page for `url` in weight order when it is a Weight sort page, else as served. Resolves to
   * { html, keep }: keep is false when the weight order could not be made, so the page as served is
   * shown but not remembered for that address.
   */
  function transform(html, url) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer;
    var timeout = new Promise(function (resolve, reject) {
      timer = setTimeout(function () {
        if (controller) controller.abort();
        reject(new Error('timed out'));
      }, TIMEOUT_MS);
    });
    var work;
    try {
      work = build(html, url, controller && controller.signal);
    } catch (error) {
      work = Promise.reject(error);
    }
    return Promise.race([work, timeout])
      .then(function (sorted) {
        return sorted ? { html: sorted, keep: true } : { html: html, keep: true };
      })
      .catch(function (error) {
        console.warn('Weight sort: showing the page as served.', error && error.message ? error.message : error);
        return { html: html, keep: false };
      })
      .then(function (result) {
        clearTimeout(timer);
        return result;
      });
  }

  window.hwWeightSort = { transform: transform, settle: settle, weightOf: weightOf, rank: rank };

  /** How long a first load keeps the grid hidden, counted from the page's own start (sections/main-collection). */
  var FIRST_LOAD_MS = 5000;

  // A first load in a Weight sort: the page arrives in name order, hidden (sections/main-collection). The
  // weight order replaces the product grid's cards only, and only while the grid is still hidden.
  //
  // Round 4 review (2026-09-29): this went through the theme's renderPage, which (1) cancelled the page's
  // 5 s limit for its own 13 s, so a slow phone saw a blank grid for up to 13 s and could then have the
  // cards reordered under its finger, and (2) replaced the whole #CollectionProductGrid, so the FAQ below
  // lost its closed state (screen readers read every answer) and focus in the toolbar was dropped.
  function start() {
    var grid = document.getElementById('main-collection-product-grid');
    var filters = window.theme && window.theme.CollectionFilters;
    if (!grid || !grid.getAttribute('data-weight-sort') || !filters || (window.Shopify && window.Shopify.designMode)) {
      settle();
      return;
    }
    // A first load of this sort and these filters that ended as served (too slow, or it failed): its other
    // pages are shown as served too, for as long as the weights are kept. Page 1 in name order and page 2
    // in weight order is neither list, so products repeated or never appeared across the pages (review,
    // rounds 5 and 6). Cleared as soon as a page of that list is drawn in weight order (build).
    var asServed = asServedKey(grid.getAttribute('data-collection-path') || window.location.pathname, grid.getAttribute('data-weight-sort'), filterQuery(new URL(window.location.href).searchParams));
    if (asServedRecent(asServed)) {
      settle();
      return;
    }
    var swapped = false;
    clearTimeout(window.hwWeightFailsafe);
    var t0 = typeof window.hwWeightT0 === 'number' ? window.hwWeightT0 : Date.now();
    var deadline = setTimeout(settle, Math.max(0, FIRST_LOAD_MS - (Date.now() - t0)));
    // The same address the theme's own renders use, so the result can be kept in its page cache.
    var search = window.location.search.replace(/^\?/, '');
    var url = window.location.pathname + '?section_id=' + encodeURIComponent(grid.getAttribute('data-id')) + (search ? '&' + search : '');
    if (!Array.isArray(filters.filterData)) filters.filterData = [];
    fetchText(url)
      .then(function (served) { return transform(served, url); })
      .then(function (result) {
        // Shown already, in the order Shopify served: kept as it is.
        if (!document.documentElement.classList.contains(PENDING) || !result.keep) return;
        var fresh = new DOMParser().parseFromString(result.html, 'text/html').getElementById('main-collection-product-grid');
        var live = document.getElementById('main-collection-product-grid');
        if (!fresh || !live) return;
        live.innerHTML = fresh.innerHTML;
        swapped = true;
        filters.filterData = filters.filterData.concat([{ html: result.html, url: url }]);
        // As the theme does after its own renders (renderSectionFromFetch, renderFilters): the new cards'
        // variant links (ProductCard) and their Quick View buttons, which the theme binds one by one.
        // Only ProductCard ran here, so on a first load in a Weight sort, and every page reached by its
        // page links, QUICK VIEW did nothing (review, round 5).
        if (window.theme && typeof window.theme.ProductCard === 'function') window.theme.ProductCard();
        var quickview = window.Shopify && window.Shopify.theme && window.Shopify.theme.quickview;
        if (quickview && typeof quickview.init === 'function') quickview.init();
      })
      .catch(function (error) {
        console.warn('Weight sort: showing the page as served.', error && error.message ? error.message : error);
      })
      .then(function () {
        clearTimeout(deadline);
        // Every way this can end without the swap: the 5 s limit passed (also before this started), the
        // build failed or timed out, the page was already shown.
        if (!swapped) markAsServed(asServed);
        settle();
      });
  }

  function asServedKey(path, dir, filters) {
    return 'hwws:asServed:' + path + '?' + dir + '&' + filters;
  }
  function asServedRecent(key) {
    try {
      var at = Number(window.sessionStorage.getItem(key));
      return at > 0 && Date.now() - at < INDEX_TTL_MS;
    } catch (e) {
      return false;
    }
  }
  function markAsServed(key) {
    try { window.sessionStorage.setItem(key, String(Date.now())); } catch (e) { /* storage off */ }
  }
  function forgetAsServed(key) {
    try { window.sessionStorage.removeItem(key); } catch (e) { /* storage off */ }
  }

  // After the theme has set the collection page up, which it does on DOMContentLoaded (assets/theme.js,
  // sections.register): this deferred script runs before that event, with the page no longer "loading",
  // so starting at once ran before the theme and did nothing (2026-09-28). Its listener was added earlier
  // than this one, so it runs first.
  if (document.readyState === 'complete') setTimeout(start, 0);
  else document.addEventListener('DOMContentLoaded', start);
})();
